import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import clsx from 'clsx';

import { Drawer, drawerType } from '../../../../../packages/ui/organisms/drawer';
import {
  Avatar,
  avatarType,
  btnType,
  Button,
  colors,
  fontWeight,
  IconButton,
  Icons,
  Input,
  inputSize,
  inputType,
  Table,
  Text,
} from '../../../../../packages';
import { profileActionCreators } from '../../../../../redux/modules/profile';
import { getProfile, selectConnections } from '../../../../../redux/modules/profile/selectors';
import { useStyles } from './AddConnection.styles';
import { memberColumns } from './AddConnection.constants';

const AddConnection = ({ isOpen, isSaving, onClose, onAddConnection }) => {
  const classes = useStyles();
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [listedMembers, setListedMembers] = useState([]);
  const [filteredCount, setFilteredCount] = useState(0);
  const [value, setValue] = useState({
    search: { searchKey: '' },
    pagination: { currentPage: 1, rowsPerPage: 10 },
    sorter: { direction: 'asc', column: '' },
  });
  const {
    isLoading,
    payload: { patientsList, totalRecords },
  } = useSelector(getProfile);
  const { connections } = useSelector(selectConnections);
  const totalPages = Math.ceil(totalRecords / value.pagination.rowsPerPage);
  const dispatch = useDispatch();

  const tableData = useMemo(
    () =>
      listedMembers.map(patient => ({
        ...patient,
        connection: patient.member,
        actions: { userId: patient.member.id },
      })),
    [listedMembers],
  );

  useEffect(() => {
    setSelectedUserId(null);
    const newMembers = patientsList.filter(
      member => !connections.some(item => item.connectionId === member.id),
    );
    const filteredNewMembers = [...listedMembers, ...newMembers].filter(
      (member, index, self) => index === self.findIndex(t => t.id === member.id),
    );
    setListedMembers([...filteredNewMembers]);
    setFilteredCount(filteredNewMembers.length);
  }, [patientsList]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedUserId(null);
    }
  }, [isOpen]);

  const handleFetchPatientsParamsFormat = ({ search, pagination, sorter }) => {
    const sortBy = sorter.column === 'member' ? 'name' : sorter.column || '';
    const params = {
      searchQuery: search.searchKey,
      pageNumber: pagination.currentPage,
      pageSize: pagination.rowsPerPage,
      orderBy: sorter.direction,
      sortBy,
    };

    return params;
  };

  useEffect(() => {
    dispatch(profileActionCreators.fetchPatients(handleFetchPatientsParamsFormat(value)));
  }, [value]);

  const handleSubmit = useCallback(() => {
    onAddConnection && onAddConnection({ userId: selectedUserId });
    setValue({
      search: { searchKey: '' },
      pagination: { currentPage: 1, rowsPerPage: 10 },
      sorter: { direction: 'asc', column: '' },
    });
  }, [onAddConnection, selectedUserId]);

  const handleClose = () => {
    onClose();
    setValue({
      search: { searchKey: '' },
      pagination: { currentPage: 1, rowsPerPage: 10 },
      sorter: { direction: 'asc', column: '' },
    });
  };

  const renderColumns = memberColumns.map(column => {
    if (column.id === 'connection') {
      return {
        ...column,
        renderCell: ({ photo, fullName, nickName }) => (
          <div className={classes.member}>
            <Avatar size={40} variant={avatarType.CIRCLE} src={photo} name={nickName} />
            <div className={classes.info}>
              <span className={classes.id}>{nickName}</span>
              <span className={classes.name}>{fullName}</span>
            </div>
          </div>
        ),
      };
    }
    if (column.id === 'actions') {
      return {
        ...column,
        renderCell: ({ userId }) => (
          <div className={classes.checkIcon}>
            {selectedUserId === userId && <Icons glyph="checkmark" color={colors.success500} />}
          </div>
        ),
      };
    }
    return column;
  });

  const onChangeSearchKey = val => {
    setValue({
      ...value,
      search: { searchKey: val },
    });
  };

  return (
    <Drawer open={isOpen} onClose={handleClose} variant={drawerType.NORMAL} className={classes.drawer}>
      <Box className={classes.wrapper}>
        <Box className={classes.header}>
          <Text weight={fontWeight.BOLD} className={classes.headTitle}>
            Add Connection
          </Text>
          <IconButton icon="close" className={classes.closeBtn} onClick={handleClose} />
        </Box>
        <Box padding={2}>
          <Input
            className={classes.inputSearch}
            variant={inputType.SEARCH}
            placeholder="Search"
            value={value.search.searchKey}
            onChange={onChangeSearchKey}
            size={inputSize.S}
            fullWidth
          />
        </Box>
        <Box className={clsx(classes.content, { [classes.showFooter]: !!selectedUserId })}>
          <Box className={classes.memberWrapper}>
            <Box className={classes.tableContainer}>
              {listedMembers.map((item, index) => (
                <Box
                  key={index}
                  className={classes.rowWapper}
                  onClick={() => setSelectedUserId(item.member.id)}
                  sx={{
                    backgroundColor: selectedUserId === item.member.id ? colors.neutral100 : 'transparent',
                  }}
                >
                  <div className={classes.member}>
                    <Avatar
                      size={40}
                      variant={avatarType.CIRCLE}
                      src={item.member.photo}
                      name={item.member.nickName}
                    />
                    <div className={classes.info}>
                      <span className={classes.id}>{item.member.nickName}</span>
                      <span className={classes.name}>{item.member.fullName}</span>
                    </div>
                  </div>
                  <div className={classes.checkIcon}>
                    {selectedUserId === item.member.id && (
                      <Icons glyph="checkmark" color={colors.success500} />
                    )}
                  </div>
                </Box>
              ))}
            </Box>
            {totalRecords !== 0 && value.pagination.currentPage <= totalPages && (
              <Box className={classes.btnContainer}>
                <Button
                  variant={btnType.TEXT}
                  className={classes.loadMoreBtn}
                  onClick={() =>
                    setValue({
                      ...value,
                      pagination: { ...value.pagination, currentPage: value.pagination.currentPage + 1 },
                    })
                  }
                >
                  Load more
                </Button>
              </Box>
            )}
          </Box>
        </Box>
        {selectedUserId && (
          <Box className={classes.footer}>
            <Button variant={btnType.TEXT} onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={!selectedUserId || isSaving}>
              {!isSaving ? 'Add connection' : 'Saving...'}
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export { AddConnection };
